import { type IPagination, PAGINATION_DOTS, paginationRangeHandler } from 'BreetHelpers';
import { useMemo } from 'react';

export const usePagination = ({ totalDocItems = 1, pageLimit, siblingCount = 1, currentPage }: IPagination) => {
	const paginationRange = useMemo(() => {
		const totalPageCount = Math.ceil(totalDocItems / pageLimit);

		// Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
		const totalPageNumbers = siblingCount + 5;

		/*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
		if (totalPageNumbers >= totalPageCount) {
			return paginationRangeHandler(1, totalPageCount);
		}

		/*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
		const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
		const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

		/*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
		const shouldShowLeftDots = leftSiblingIndex > 2;
		const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

		const firstPageIndex = 1;
		const lastPageIndex = totalPageCount;

		/*
    	Case 2: No left dots to show, but rights dots to be shown
    */
		if (!shouldShowLeftDots && shouldShowRightDots) {
			const leftItemCount = 3 + 2 * siblingCount;
			const leftRange = paginationRangeHandler(1, leftItemCount);

			return [...leftRange, PAGINATION_DOTS, totalPageCount];
		}
		if (shouldShowLeftDots && !shouldShowRightDots) {
			/*
        Case 3: No right dots to show, but left dots to be shown
      */
			const rightItemCount = 3 + 2 * siblingCount;
			const rightRange = paginationRangeHandler(totalPageCount - rightItemCount + 1, totalPageCount);
			return [firstPageIndex, PAGINATION_DOTS, ...rightRange];
		}

		/*
        Case 4: Both left and right dots to be shown
      */
		const middleRange = paginationRangeHandler(leftSiblingIndex, rightSiblingIndex);
		return [firstPageIndex, PAGINATION_DOTS, ...middleRange, PAGINATION_DOTS, lastPageIndex];
	}, [totalDocItems, pageLimit, siblingCount, currentPage]);

	return paginationRange;
};
